import { Select, SwitchField } from '@/components/ui/controls'
import { Field } from '@/components/ui/field'
import { GOVERNANCE_ROLES, VISIBILITIES, type OrgSettings } from '@/data/settings'
import { Consequence, SettingsCard, SwitchGroup } from './SettingsCard'

/**
 * Who can see a recorded meeting, and who can destroy one.
 *
 * They are separate cards because they are separate risks: the first is about
 * reach, the second is irreversible. Both are scoped narrowly on purpose —
 * neither controls sharing, which anyone who can view a meeting can already do.
 */
export function AccessSettings({
  draft,
  onChange,
}: {
  draft: OrgSettings
  onChange: (next: OrgSettings) => void
}) {
  const { visibility, deletion } = draft

  const setVisibility = (patch: Partial<OrgSettings['visibility']>) =>
    onChange({ ...draft, visibility: { ...visibility, ...patch } })
  const setDeletion = (patch: Partial<OrgSettings['deletion']>) =>
    onChange({ ...draft, deletion: { ...deletion, ...patch } })

  const adminOnly = visibility.managementRole === 'org_admin'

  return (
    <>
      <SettingsCard title="Meeting visibility" meta="applies to new recordings">
        <Field label="New recorded meetings start as">
          {(props) => (
            <Select
              id={props.id}
              value={visibility.defaultVisibility}
              onValueChange={(value) => setVisibility({ defaultVisibility: value })}
              options={VISIBILITIES}
              className="sm:max-w-sm"
            />
          )}
        </Field>

        <Field
          label="Who can change it"
          hint="Only the private/organisation switch. Sharing a meeting with a named person is not governed here."
        >
          {(props) => (
            <Select
              id={props.id}
              value={visibility.managementRole}
              onValueChange={(value) => setVisibility({ managementRole: value })}
              options={GOVERNANCE_ROLES}
              className="sm:max-w-sm"
            />
          )}
        </Field>

        <SwitchGroup>
          <SwitchField
            label="Viewers may take a meeting private"
            hint={
              adminOnly
                ? 'Not applicable while only admins can change visibility.'
                : 'Organisation admins can always do this, whatever this is set to.'
            }
            checked={visibility.allowViewerPrivateOverrides}
            disabled={adminOnly}
            onCheckedChange={(value) => setVisibility({ allowViewerPrivateOverrides: value })}
          />
        </SwitchGroup>
      </SettingsCard>

      <SettingsCard title="Deletion policy" meta="permanent, not a bin">
        <SwitchGroup>
          <SwitchField
            label="Allow meetings to be deleted"
            hint="Off blocks deletion everywhere, including the API."
            checked={deletion.enabled}
            onCheckedChange={(value) => setDeletion({ enabled: value })}
          />
        </SwitchGroup>

        {deletion.enabled ? (
          <>
            <Field label="Who can delete">
              {(props) => (
                <Select
                  id={props.id}
                  value={deletion.allowedRole}
                  onValueChange={(value) => setDeletion({ allowedRole: value })}
                  options={GOVERNANCE_ROLES}
                  className="sm:max-w-sm"
                />
              )}
            </Field>
            <Consequence>
              Deleting takes the transcript, the recording, every note and every
              share with it, in every place they were copied to. There is no
              undo and no retention window.
            </Consequence>
          </>
        ) : (
          <Consequence>
            Nobody can delete a meeting, including organisation admins. Turn this
            back on to allow it again.
          </Consequence>
        )}
      </SettingsCard>
    </>
  )
}
